import { useCallback, useRef, useState } from 'react';

import type { SavedConfig } from '../../types/caller.js';
import { extractCv } from '../../utils/sigsParser.js';

export interface ApiCallResult {
  status: number;
  statusText: string;
  headers: Record<string, string>;
  body: string;
  durationMs: number;
  cv: string | null;
}

interface UseApiCallResult {
  result: ApiCallResult | null;
  loading: boolean;
  error: string | null;
  varErrors: string[];
  execute: (
    config: SavedConfig,
    emitSigsLocally?: boolean,
  ) => Promise<ApiCallResult | null>;
  cancel: () => void;
}

function resolveVars(
  text: string,
  vars: SavedConfig['vars'],
): { resolved: string; errors: string[] } {
  const errors: string[] = [];
  const resolved = text.replace(/\{\{([^}]+)\}\}/g, (_, name: string) => {
    const trimmed = name.trim();
    const v = vars.find((v) => v.enabled && v.key === trimmed);
    if (!v) {
      errors.push(`Unknown variable: {{${trimmed}}}`);
      return `{{${trimmed}}}`;
    }
    return v.value;
  });
  return { resolved, errors };
}

export function useApiCall(): UseApiCallResult {
  const [result, setResult] = useState<ApiCallResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [varErrors, setVarErrors] = useState<string[]>([]);
  const abortRef = useRef<AbortController | null>(null);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const execute = useCallback(
    async (
      config: SavedConfig,
      emitSigsLocally = false,
    ): Promise<ApiCallResult | null> => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);
      setError(null);
      setVarErrors([]);
      const start = Date.now();

      // Resolve {{varName}} in URL and body
      const allVarErrors: string[] = [];
      const { resolved: resolvedUrl, errors: urlErrors } = resolveVars(
        config.url,
        config.vars ?? [],
      );
      allVarErrors.push(...urlErrors);
      const { resolved: resolvedBody, errors: bodyErrors } = resolveVars(
        config.body,
        config.vars ?? [],
      );
      allVarErrors.push(...bodyErrors);

      if (allVarErrors.length > 0) {
        setVarErrors(allVarErrors);
        setLoading(false);
        return null;
      }

      try {
        // Build headers — inject emitSigsLocally into x-client-metadata if needed
        const headersMap: Record<string, string> = {};
        for (const h of config.headers) {
          if (!h.enabled || !h.key) continue;
          if (emitSigsLocally && h.key === 'x-client-metadata') {
            try {
              const meta = JSON.parse(h.value) as Record<string, unknown>;
              meta.emitSigsLocally = true;
              headersMap[h.key] = JSON.stringify(meta);
            } catch {
              headersMap[h.key] = h.value;
            }
          } else {
            headersMap[h.key] = h.value;
          }
        }

        // Build URL with query params
        let urlStr = resolvedUrl;
        const enabledParams = config.params.filter((p) => p.enabled && p.key);
        if (enabledParams.length > 0) {
          const url = new URL(urlStr);
          for (const p of enabledParams) {
            url.searchParams.set(p.key, p.value);
          }
          urlStr = url.toString();
        }

        const init: RequestInit = {
          method: config.method,
          headers: headersMap,
          signal: controller.signal,
        };

        if (config.method !== 'GET' && resolvedBody.trim()) {
          init.body = resolvedBody;
        }

        const res = await fetch(urlStr, init);
        const durationMs = Date.now() - start;

        const responseHeaders: Record<string, string> = {};
        res.headers.forEach((v, k) => {
          responseHeaders[k] = v;
        });

        const body = await res.text();

        // Try to extract CV — check response headers first, then body
        const CV_HEADERS = ['ms-cv', 'x-ms-cv', 'x-cv', 'cv'];
        let cv: string | null =
          CV_HEADERS.map((h) => responseHeaders[h]).find((v) => v != null) ??
          null;
        if (!cv) {
          try {
            const parsed: unknown = JSON.parse(body);
            cv = extractCv(parsed);
          } catch {
            // not JSON, no CV to extract
          }
        }

        const r: ApiCallResult = {
          status: res.status,
          statusText: res.statusText,
          headers: responseHeaders,
          body,
          durationMs,
          cv,
        };
        setResult(r);
        return r;
      } catch (e) {
        if (e instanceof DOMException && e.name === 'AbortError') {
          return null;
        }
        setError(String(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { result, loading, error, varErrors, execute, cancel };
}
